import type { MetaFunction } from "react-router";
import { Link } from "react-router";
import { SITE_ORIGIN } from "../brand";
import {
  APP_STORE_URL,
  marketingLinks,
  MarketingShell,
  socialImageMeta,
} from "../components/marketing/layout";
import {
  ArrowRight,
  Check,
  Download,
  FlaskConical,
  Leaf,
  ListChecks,
  Package,
  Pill,
  Printer,
  Scale,
  Search,
  ShieldCheck,
  Store,
} from "../components/marketing/icons";

const PAGE_URL = `${SITE_ORIGIN}/features`;

export const links = marketingLinks;

export const meta: MetaFunction = () => [
  { title: "EasyNutriFact Features — FDA-Style Label Panels" },
  {
    name: "description",
    content:
      "Create Nutrition Facts and Supplement Facts panels in Shopify with live previews, rounding, Daily Values, USDA lookup, and PNG export.",
  },
  { name: "robots", content: "index,follow" },
  { tagName: "link", rel: "canonical", href: PAGE_URL },
  { property: "og:url", content: PAGE_URL },
  { property: "og:type", content: "website" },
  { property: "og:title", content: "EasyNutriFact — full feature breakdown" },
  {
    property: "og:description",
    content:
      "Two panel formats, automatic 21 CFR rounding, USDA lookup, 300 DPI export, and labels that live on your own store. Free.",
  },
  ...socialImageMeta(),
  {
    "script:ld+json": {
      "@context": "https://schema.org",
      "@type": "WebPage",
      name: "EasyNutriFact features",
      url: PAGE_URL,
      description:
        "Nutrition Facts and Supplement Facts panels, live preview, automatic FDA rounding, USDA lookup, and 300 DPI PNG export inside Shopify admin.",
      publisher: { "@type": "Organization", name: "Legitimate LLC" },
    },
  },
];

const PANEL_FEATURES = [
  {
    icon: Leaf,
    title: "Nutrition Facts (21 CFR 101.9)",
    body: "The standard US retail panel: serving size with household measure and grams, servings per container, calories, the full mandatory macro list, and the four mandatory micronutrients (vitamin D, calcium, iron, potassium). Bold rules, indents, and the footnote are all laid out for you.",
  },
  {
    icon: Pill,
    title: "Supplement Facts (21 CFR 101.36)",
    body: "The dietary-supplement format: dosage-form servings (“2 capsules”), dietary ingredients with part-of-plant, core nutrients with automatic %DV, and proprietary blends with the dagger footnote the format requires.",
  },
  {
    icon: Scale,
    title: "Automatic FDA rounding",
    body: "Every display rule from 21 CFR 101.9(c) is applied by the engine: calories to the nearest 5 or 10, fat in 0.5 g steps below 5 g, sodium to the nearest 5 or 10 mg, “less than 1 g” thresholds — and %DV computed from the unrounded value, as the regulation requires.",
  },
  {
    icon: ListChecks,
    title: "% Daily Values, computed",
    body: "The 2016 Daily Value tables are built in — fat 78 g, sodium 2,300 mg, added sugars 50 g, and the rest. Macros display to the nearest 1%; micronutrients follow the 2/5/10 reporting grid with the below-2% omission rule.",
  },
];

const WORKFLOW_FEATURES = [
  {
    icon: Package,
    title: "Lives in Shopify admin",
    body: "No separate app to log into. Pick a product from your catalog, and the editor opens with every field the panel expects — the preview builds live on the right as you type.",
  },
  {
    icon: Search,
    title: "USDA FoodData Central lookup",
    body: "Search the USDA’s database from inside the editor and prefill per-serving nutrient values from your ingredients. A genuine head start when you don’t have lab numbers — and clearly labeled as a starting point, not an analysis.",
  },
  {
    icon: Printer,
    title: "300 DPI print-ready PNG export",
    body: "The export is rasterized from the same renderer as the live preview, so what you see is what prints: white background, 300 DPI, ready for packaging artwork, the product page, or marketplace listings (Amazon, TikTok Shop, Walmart).",
  },
  {
    icon: Store,
    title: "Labels stored on your store",
    body: "Each saved label is a Shopify metaobject attached to its product — visible under Content → Metaobjects, portable, and yours. Uninstall and reinstall: the labels are still there. One in-app control removes everything.",
  },
];

const ASSURANCES = [
  {
    icon: ShieldCheck,
    title: "Your data stays yours",
    body: "Label content lives on your store, not in our database. We keep only a lightweight activity record (timestamp + product reference) for your history list, hard-deleted 30 days after uninstall — and we honor all three Shopify GDPR webhooks.",
  },
  {
    icon: FlaskConical,
    title: "Honest about what it isn’t",
    body: "EasyNutriFact is a formatting and calculation tool. It does not test recipes, run lab analysis, or certify compliance — the app says so on every surface, and your final label should always be verified by a qualified labeling expert.",
  },
];

export default function Features() {
  return (
    <MarketingShell>
      <section className="nm-hero">
        <div className="nm-wrap">
          <div className="nm-head">
            <p className="nm-eyebrow nm-reveal" data-d="1">
              Features
            </p>
            <h1
              className="nm-reveal"
              data-d="2"
              style={{ fontSize: "var(--step-4)", marginTop: "1.2rem" }}
            >
              Everything a correct panel needs. Nothing a merchant doesn&rsquo;t.
            </h1>
            <p className="nm-hero__lede nm-reveal" data-d="3">
              EasyNutriFact is deliberately narrow: it builds the two US label
              panels food and supplement brands actually print, applies the rules
              that make them correct, and gets out of the way.
            </p>
            <div className="nm-hero__cta nm-reveal" data-d="4">
              <a className="nm-btn" href={APP_STORE_URL}>
                Install free on Shopify
                <ArrowRight />
              </a>
              <Link className="nm-btn nm-btn--ghost" to="/demo">
                Try the live demo
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="nm-section nm-band" style={{ paddingTop: "clamp(2.5rem, 1.5rem + 4vw, 4.5rem)" }}>
        <div className="nm-wrap">
          <div className="nm-head">
            <p className="nm-eyebrow">The panels</p>
            <h2>Two formats, both by the book</h2>
          </div>
          <div className="nm-cards" style={{ marginTop: "2.6rem" }}>
            {PANEL_FEATURES.map((f) => (
              <article className="nm-card" key={f.title}>
                <span className="nm-card__icon">
                  <f.icon />
                </span>
                <h3>{f.title}</h3>
                <p>{f.body}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="nm-section">
        <div className="nm-wrap">
          <div className="nm-head">
            <p className="nm-eyebrow">The workflow</p>
            <h2>From product to print without leaving admin</h2>
          </div>
          <div className="nm-cards" style={{ marginTop: "2.6rem" }}>
            {WORKFLOW_FEATURES.map((f) => (
              <article className="nm-card" key={f.title}>
                <span className="nm-card__icon">
                  <f.icon />
                </span>
                <h3>{f.title}</h3>
                <p>{f.body}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="nm-section nm-band">
        <div className="nm-wrap">
          <div className="nm-head">
            <p className="nm-eyebrow">The fine print, up front</p>
            <h2>What we promise — and what we don&rsquo;t</h2>
          </div>
          <div className="nm-cards" style={{ marginTop: "2.6rem" }}>
            {ASSURANCES.map((f) => (
              <article className="nm-card" key={f.title}>
                <span className="nm-card__icon">
                  <f.icon />
                </span>
                <h3>{f.title}</h3>
                <p>{f.body}</p>
              </article>
            ))}
            <article className="nm-card">
              <span className="nm-card__icon">
                <Download />
              </span>
              <h3>Free means free</h3>
              <p>
                Every feature above is in the single free plan — no label caps,
                no watermarks, no export fee. The pricing page says it plainly,
                because there&rsquo;s nothing to hide.
              </p>
              <p style={{ marginTop: "1rem" }}>
                <Link className="nm-textlink" to="/pricing">
                  See pricing
                  <ArrowRight width={16} height={16} />
                </Link>
              </p>
            </article>
          </div>
        </div>
      </section>

      <section className="nm-section">
        <div className="nm-wrap nm-wrap--narrow">
          <div className="nm-head nm-head--center">
            <p className="nm-eyebrow nm-eyebrow--center">At a glance</p>
            <h2>The whole spec sheet</h2>
          </div>
          <div className="nm-tablewrap">
            <table className="nm-table">
              <thead>
                <tr>
                  <th scope="col">Capability</th>
                  <th scope="col">EasyNutriFact</th>
                </tr>
              </thead>
              <tbody>
                {[
                  ["Nutrition Facts panel (21 CFR 101.9)", true],
                  ["Supplement Facts panel (21 CFR 101.36)", true],
                  ["Live preview in Shopify admin", true],
                  ["Automatic FDA rounding + % Daily Values", true],
                  ["USDA FoodData Central lookup", true],
                  ["300 DPI print-ready PNG export", true],
                  ["Labels saved as metaobjects on your store", true],
                  ["Price", "Free"],
                ].map(([cap, val]) => (
                  <tr key={cap as string}>
                    <th scope="row">{cap}</th>
                    <td>
                      {val === true ? (
                        <span className="nm-yes">
                          <Check width={14} height={14} style={{ display: "inline", verticalAlign: "-2px" }} /> Included
                        </span>
                      ) : (
                        <strong>{val}</strong>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <section className="nm-section">
        <div className="nm-wrap">
          <div className="nm-cta nm-reveal" data-d="1">
            <h2>See it build a panel in your admin</h2>
            <p>
              Install free, pick a product, and type a calorie value. The panel
              does the rest.
            </p>
            <div className="nm-cta__actions">
              <a className="nm-btn" href={APP_STORE_URL}>
                Install free on Shopify
                <ArrowRight />
              </a>
              <Link className="nm-btn nm-btn--ghost" to="/how-to-build-an-fda-nutrition-facts-label">
                Read the label guide
              </Link>
            </div>
          </div>
        </div>
      </section>
    </MarketingShell>
  );
}
